'use client';

import { useEffect, useRef, useState } from 'react';

import { CourseCardActionButton } from '@/features/course-card/components/CourseCardActionButton';
import { CourseCardCartIconButton } from '@/features/course-card/components/CourseCardCartIconButton';
import type { CourseCardCartState } from '@/features/course-card/types/course-card';

const FLASH_ADDED_TIMEOUT_MS = 2000;

export type CartButtonVariant = 'wide' | 'icon';

export interface CartButtonWidgetProps {
  variant?: CartButtonVariant;
  inCart?: boolean;
  disabled?: boolean;
  hideIdleIcon?: boolean;
  idleLabel?: string;
  payLabel?: string;
  className?: string;
  'aria-label'?: string;
  onAddToCart?: () => void;
  onCheckout?: () => void;
  onAddAnimationComplete?: () => void;
}

export function CartButtonWidget({
  variant = 'wide',
  inCart = false,
  disabled = false,
  hideIdleIcon = false,
  idleLabel = 'Agregar al carrito',
  payLabel = 'Ir a pagar',
  className,
  'aria-label': ariaLabel,
  onAddToCart,
  onCheckout,
  onAddAnimationComplete,
}: CartButtonWidgetProps) {
  const [flashAdded, setFlashAdded] = useState(false);
  const flashTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
    },
    [],
  );

  const handleClick = () => {
    if (inCart) {
      onCheckout?.();
      return;
    }

    onAddToCart?.();
    setFlashAdded(true);

    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => {
      setFlashAdded(false);
      flashTimer.current = null;
    }, FLASH_ADDED_TIMEOUT_MS);
  };

  let cartState: CourseCardCartState = 'idle';
  if (inCart) {
    cartState = flashAdded ? 'flash' : 'pay';
  } else if (flashAdded) {
    cartState = 'flash';
  }

  if (variant === 'icon') {
    return (
      <CourseCardCartIconButton
        aria-label={ariaLabel ?? idleLabel}
        cartState={cartState}
        className={className}
        onAddingAnimationComplete={onAddAnimationComplete}
        onClick={handleClick}
      />
    );
  }

  return (
    <CourseCardActionButton
      aria-label={ariaLabel}
      cartState={cartState}
      className={className}
      disabled={disabled}
      hideIdleIcon={hideIdleIcon}
      idleLabel={idleLabel}
      payLabel={payLabel}
      onCartFlashAnimationComplete={onAddAnimationComplete}
      onClick={handleClick}
    />
  );
}
